import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";
import { useForm, Controller } from "react-hook-form";
import { useReportsStore } from "../../../store/reports/reportsStore";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAlert } from "../../../context/AlertContext";
import InfiniteDeviceSelect, { useReportDeviceIds } from "../../../components/InfiniteDeviceSelect";
import { useReportTrackingStore } from "../../../store/reports/reportTrackingStore";
import { allDevicesQueryOptions } from "../../../features/devices/queries";
import { useReportCompanyScope } from "../hooks/useReportCompanyScope";
import ReportRouteMap, { type SelectedRoutePoint } from "../components/ReportRouteMap";
import ReportPointsTable, { type ReportTableColumn } from "../components/ReportPointsTable";
import { buildHistoricRows, type HistoricRow } from "../utils/reportPointProcessors";
import { exportHistoricToExcel, exportHistoricToPdf } from "../utils/exportHistoric";
import ReportActionButton from "../components/ReportActionButton";
import ReportDateRangePicker from "../components/ReportDateRangePicker";
import ReportFormatToggle from "../components/ReportFormatToggle";
import ReportSwitch from "../components/ReportSwitch";
import { toLocalInputValue } from "../utils/reportDateFormat";
import { fetchArchiveCoverage, summarizeCoverage } from "../../../features/device-archive/jobs";
import { useIsReseller } from "../../../hooks/useIsReseller";
import { Link } from "react-router-dom";

const MAX_TRACKING_RANGE_DAYS = 31;
const PDF_WARN_THRESHOLD = 20000;

interface HistoryFormData {
  devices: string[];
  startDate: string;
  endDate: string;
  typeReport: string;
  typeDocument: "excel" | "pdf";
  withGeocoding: boolean;
}

const HISTORIC_COLUMNS: ReportTableColumn<HistoricRow>[] = [
  { key: "unidad", header: "Unidad" },
  { key: "orden", header: "Orden", align: "center" },
  { key: "fecha", header: "Última posición" },
  { key: "estadoMotor", header: "Estado del motor" },
  { key: "latitud", header: "Latitud", align: "right" },
  { key: "longitud", header: "Longitud", align: "right" },
  { key: "orientacion", header: "Orientación" },
  { key: "velocidad", header: "Velocidad", align: "right" },
  { key: "bateria", header: "Nivel de batería", align: "right" },
];

export default function HistorySection() {

  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);
  const generateHistoryReport = useReportsStore((state) => state.generateHistoryReport);
  const loading = useReportsStore((state) => state.loading);
  const { loadedDeviceIds, loadedCount, totalDevices } = useReportDeviceIds();

  const loadRange = useReportTrackingStore((state) => state.loadRange);
  const clearTracking = useReportTrackingStore((state) => state.clear);
  const routesState = useReportTrackingStore((state) => state.routes);
  const order = useReportTrackingStore((state) => state.order);
  const trackingLoading = useReportTrackingStore((state) => state.loading);
  const addresses = useReportTrackingStore((state) => state.addresses);
  const tz = useReportTrackingStore((state) => state.tz);

  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);
  const [routeLoadAttempted, setRouteLoadAttempted] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState<SelectedRoutePoint | null>(null);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm<HistoryFormData>({
    mode: "onBlur",
    defaultValues: {
      devices: [],
      startDate: "",
      endDate: "",
      typeReport: "historic",
      typeDocument: "pdf",
      withGeocoding: false,
    },
  });

  const { showAlert } = useAlert();

  const { companyId, disableWithoutCompany } = useReportCompanyScope(
    setValue,
    () => {
      clearTracking();
      setRouteLoadAttempted(false);
      setSelectedPoint(null);
    },
  );
  const { data: allDevices } = useQuery({
    ...allDevicesQueryOptions(companyId),
  });

  const selectedDevices = watch("devices");
  const startDate = watch("startDate");
  const endDate = watch("endDate");

  const hasDevices = (selectedDevices?.length ?? 0) > 0;
  const hasDates = Boolean(startDate && endDate);
  const rangeDays = useMemo(() => {
    if (!hasDates) return 0;
    return (new Date(endDate).getTime() - new Date(startDate).getTime()) / (24 * 60 * 60 * 1000);
  }, [endDate, hasDates, startDate]);
  const hasValidRange = hasDates && rangeDays >= 0;
  const isWithinTrackingLimit = hasValidRange && rangeDays <= MAX_TRACKING_RANGE_DAYS;
  const filtersReadyForRoute = hasDevices && hasValidRange && isWithinTrackingLimit;
  const filtersReadyForReport = hasDevices && hasValidRange;

  const handleSelectAll = () => {
    if (selectedDevices?.length === loadedDeviceIds.length) {
      setValue("devices", []);
    } else {
      setValue("devices", loadedDeviceIds);
    }
  };

  const onSubmit = async (formData: HistoryFormData) => {
    const result = await generateHistoryReport({
      deviceIds: formData.devices,
      startDate: formData.startDate,
      endDate: formData.endDate,
      typeReport: formData.typeReport,
      typeDocument: formData.typeDocument,
      withGeocoding: formData.withGeocoding,
    }, showAlert, () => { });

    if (result.success && result.data) {
      const extension = formData.typeDocument === "pdf" ? "pdf" : "xlsx";
      const blob = new Blob([result.data], {
        type: formData.typeDocument === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.fileName || `reporte.${extension}`;
      link.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const orderedRoutes = useMemo(
    () => order.map((id) => routesState[id]).filter(Boolean),
    [order, routesState],
  );

  const mapRoutes = useMemo(
    () =>
      orderedRoutes.map((r) => ({
        deviceId: r.deviceId,
        vehicleName: r.vehicleName,
        color: r.color,
        points: r.points,
      })),
    [orderedRoutes],
  );

  const hasAddresses = Object.keys(addresses).length > 0;

  const tableColumns = useMemo<ReportTableColumn<HistoricRow>[]>(
    () =>
      hasAddresses
        ? [...HISTORIC_COLUMNS, { key: "direccion", header: "Dirección" }]
        : HISTORIC_COLUMNS,
    [hasAddresses],
  );

  const tableRows = useMemo(
    () =>
      buildHistoricRows(
        orderedRoutes,
        tz || Intl.DateTimeFormat().resolvedOptions().timeZone,
        hasAddresses ? addresses : undefined,
      ),
    [orderedRoutes, tz, hasAddresses, addresses],
  );

  const resolveNameById = useCallback((): Record<string, string> => {
    const map: Record<string, string> = {};
    (allDevices ?? []).forEach((d: any) => {
      map[d.id] = d.vehicle?.name || d.identifier || d.id;
    });
    return map;
  }, [allDevices]);

  const resolveBrandById = useCallback(
    (): Record<string, { brandName: string | null; brandModel: string | null }> => {
      const map: Record<string, { brandName: string | null; brandModel: string | null }> = {};
      (allDevices ?? []).forEach((d: any) => {
        map[d.id] = {
          brandName: d.network?.brand_name ?? null,
          brandModel: d.network?.model_name ?? d.model ?? null,
        };
      });
      return map;
    },
    [allDevices],
  );

  const onViewRoute = async () => {
    const { devices, startDate, endDate, withGeocoding } = getValues();

    if (!devices || devices.length === 0) {
      showAlert({
        title: "Selecciona unidades",
        text: "Selecciona al menos un equipo para ver el recorrido.",
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
      return;
    }

    if (!startDate || !endDate) {
      showAlert({
        title: "Fechas requeridas",
        text: "Selecciona la fecha de inicio y fin.",
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
      return;
    }

    const rangeDays =
      (new Date(endDate).getTime() - new Date(startDate).getTime()) / (24 * 60 * 60 * 1000);
    if (rangeDays > MAX_TRACKING_RANGE_DAYS) {
      showAlert({
        title: "Rango demasiado amplio",
        text: `Ver recorrido solo muestra hasta ${MAX_TRACKING_RANGE_DAYS} días. Para rangos mayores usa "Generar Reporte".`,
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
      return;
    }

    const fromIso = new Date(startDate).toISOString();
    const toIso = new Date(endDate).toISOString();

    const nameById = resolveNameById();
    const brandById = resolveBrandById();

    const devicesToLoad = devices.map((id) => ({
      deviceId: id,
      vehicleName: nameById[id] || id,
      brandName: brandById[id]?.brandName ?? null,
      brandModel: brandById[id]?.brandModel ?? null,
    }));

    setSelectedPoint(null);
    await loadRange(devicesToLoad, fromIso, toIso, { address: withGeocoding });
    setRouteLoadAttempted(true);
  };

  const onRowClick = useCallback((row: HistoricRow) => {
    setSelectedPoint((prev) =>
      prev?.vehicleName === row.unidad && prev?.ts === row.ts
        ? null
        : {
            vehicleName: row.unidad,
            ts: row.ts,
            lat: row.lat,
            lng: row.lng,
            fecha: row.fecha,
            velocidad: row.velocidad,
            status: row.status,
          },
    );
  }, []);

  const isRowSelected = useCallback(
    (row: HistoricRow) =>
      selectedPoint != null &&
      selectedPoint.vehicleName === row.unidad &&
      selectedPoint.ts === row.ts,
    [selectedPoint],
  );

  const clearSelectedPoint = useCallback(() => setSelectedPoint(null), []);

  const onDownload = async (typeDocument: "pdf" | "excel") => {
    if (orderedRoutes.length === 0) return;

    const { startDate, endDate } = getValues();
    const extension = typeDocument === "pdf" ? "pdf" : "xlsx";
    const fileName = `historic_${startDate.split("T")[0]}_${endDate.split("T")[0]}.${extension}`;
    const period = `Período: ${startDate.replace("T", " ")} al ${endDate.replace("T", " ")}`;

    const run = async () => {
      setExporting(typeDocument);
      await new Promise((r) => setTimeout(r, 50));
      try {
        const meta = { title: "Reporte Histórico", period, fileName };
        if (typeDocument === "excel") {
          await exportHistoricToExcel(tableRows, tableColumns, meta);
        } else {
          exportHistoricToPdf(tableRows, tableColumns, meta);
        }
      } catch {
        showAlert({
          title: "Error al descargar",
          text: "No se pudo generar el archivo del recorrido.",
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#2563eb",
          showCancelButton: false,
        });
      } finally {
        setExporting(null);
      }
    };

    if (typeDocument === "pdf" && tableRows.length > PDF_WARN_THRESHOLD) {
      showAlert({
        title: "El PDF será muy grande",
        text: `El recorrido tiene ${tableRows.length} filas; el PDF puede tardar y pesar mucho. Para este volumen es mejor Excel. ¿Continuar de todas formas?`,
        icon: "warning",
        confirmButtonText: "Continuar",
        confirmButtonColor: "#2563eb",
        showCancelButton: true,
        onConfirm: () => {
          run();
        },
      });
      return;
    }

    await run();
  };

  useEffect(() => {
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startDateStr = toLocalInputValue(firstDayOfMonth);
    const endDateStr = toLocalInputValue(now);
    setValue("startDate", startDateStr);
    setValue("endDate", endDateStr);
  }, [setValue]);

  useEffect(() => {
    return () => {
      clearTracking();
      setRouteLoadAttempted(false);
    };
  }, [clearTracking]);

  // Fechas que ya no están en vivo sino en un respaldo (suspensión con purga):
  // sin este aviso el informe salía en blanco y soporte no sabía por qué (#741, #752).
  const { isReseller } = useIsReseller();
  const dmy = (d: string) => d.split("-").reverse().join("/");
  const coverageFrom = startDate ? startDate.slice(0, 10) : "";
  const coverageTo = endDate ? endDate.slice(0, 10) : "";
  const { data: coverage } = useQuery({
    queryKey: ["archive-coverage", companyId, selectedDevices, coverageFrom, coverageTo],
    queryFn: () => fetchArchiveCoverage(selectedDevices, coverageFrom, coverageTo),
    enabled: hasDevices && hasDates && Boolean(companyId),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const backup = summarizeCoverage(coverage ?? []);

  const hasResults = orderedRoutes.length > 0;
  const totalPoints = orderedRoutes.reduce((acc, r) => acc + r.points.length, 0);
  const showSkeleton = trackingLoading && totalPoints === 0;
  const showEmptyState = !hasResults && !showSkeleton;

  const hintCardClass = (active: boolean) =>
    `p-3 rounded-xl border ${
      active
        ? "bg-primary-500/10 border-primary-500/30"
        : isDarkMode
          ? "bg-dark-card border-dark-border"
          : "bg-gray-100 border-gray-200"
    }`;

  const hintTitleClass = (active: boolean) =>
    `text-xs font-medium ${active ? "text-primary-500" : themeClasses.textSecondary}`;

  const filterItems = [
    {
      ok: hasDevices,
      label: hasDevices
        ? `${selectedDevices.length} equipo(s) seleccionado(s)`
        : "Selecciona al menos un equipo",
    },
    {
      ok: hasDates,
      label: hasDates ? "Fechas de inicio y fin definidas" : "Define fecha de inicio y fin",
    },
    {
      ok: hasValidRange,
      label: hasValidRange
        ? `Rango: ${Math.max(1, Math.ceil(rangeDays))} día(s)`
        : "La fecha fin debe ser posterior a la de inicio",
    },
    {
      ok: isWithinTrackingLimit,
      label: isWithinTrackingLimit
        ? `Dentro del límite de Ver recorrido (${MAX_TRACKING_RANGE_DAYS} días)`
        : `Rango mayor a ${MAX_TRACKING_RANGE_DAYS} días — usa Generar Reporte`,
    },
  ];

  return (
    <div className="h-full w-full overflow-y-auto p-2 sm:p-4">
      <form onSubmit={handleSubmit(onSubmit)} className={`flex flex-col lg:flex-row ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-3.5 gap-3 justify-between lg:items-end`}>
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4 w-full lg:w-auto">
          <div className="flex flex-col gap-1 min-w-52 w-full lg:w-auto">
            <div className="flex justify-between items-center">
              <label htmlFor="devices" className={`${themeClasses.text} text-xs`}>
                Equipos {selectedDevices?.length > 0 && `(${selectedDevices.length}/${totalDevices})`}
              </label>
              <button
                type="button"
                onClick={handleSelectAll}
                disabled={disableWithoutCompany}
                className={`text-xs px-2 py-1 rounded ${themeClasses.buttonBg} text-white hover:opacity-80 disabled:opacity-50`}
              >
                {selectedDevices?.length === loadedDeviceIds.length && loadedCount > 0 ? "Deseleccionar todos" : `Seleccionar todos (${loadedCount})`}
              </button>
            </div>
            <div>
              <Controller
                name="devices"
                control={control}
                rules={{ required: "Selecciona al menos un equipo" }}
                render={({ field }) => (
                  <InfiniteDeviceSelect
                    value={field.value}
                    onChange={field.onChange}
                    isDarkMode={isDarkMode}
                    themeClasses={themeClasses}
                  />
                )}
              />
              {errors.devices && <span className="text-red-500 text-sm">{errors.devices.message}</span>}
            </div>
          </div>
          <div className="flex flex-col gap-1 w-full lg:w-auto">
            <label className={`${themeClasses.text} text-xs`}>Rango de fechas</label>
            <input type="hidden" {...register("startDate", { required: "Fecha inicio requerida" })} />
            <input type="hidden" {...register("endDate", { required: "Fecha fin requerida" })} />
            <ReportDateRangePicker
              startValue={watch("startDate")}
              endValue={watch("endDate")}
              onChangeStart={(v) => setValue("startDate", v, { shouldValidate: true, shouldDirty: true })}
              onChangeEnd={(v) => setValue("endDate", v, { shouldValidate: true, shouldDirty: true })}
              startError={errors.startDate?.message}
              endError={errors.endDate?.message}
            />
          </div>
          <div className="flex w-full items-stretch gap-3 sm:gap-4 lg:contents">
            <div className="flex flex-col gap-1 min-w-0 flex-1 lg:flex-none">
              <label htmlFor="withGeocoding" className={`${themeClasses.text} text-xs`}>Geocodificar direcciones</label>
              <div className="flex flex-col gap-1">
                <Controller
                  name="withGeocoding"
                  control={control}
                  render={({ field }) => (
                    <div className="flex h-10 items-center">
                      <ReportSwitch id="withGeocoding" checked={Boolean(field.value)} onChange={field.onChange} ariaLabel="Geocodificar direcciones" />
                    </div>
                  )}
                />
                {errors.withGeocoding && <span className="text-red-500 text-sm">{errors.withGeocoding.message}</span>}
              </div>
            </div>
            <div className="flex flex-col gap-1 min-w-0 flex-1 lg:flex-none">
              <label className={`${themeClasses.text} text-xs`}>Formato</label>
              <Controller
                name="typeDocument"
                control={control}
                render={({ field }) => <ReportFormatToggle value={field.value} onChange={field.onChange} />}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 w-full lg:w-auto">
          <ReportActionButton
            type="button"
            variant="secondary"
            onClick={onViewRoute}
            disabled={trackingLoading || !filtersReadyForRoute || disableWithoutCompany}
            title={
              !filtersReadyForRoute && hasDevices && hasValidRange && !isWithinTrackingLimit
                ? `Ver recorrido solo admite hasta ${MAX_TRACKING_RANGE_DAYS} días`
                : undefined
            }
          >
            {trackingLoading ? "Cargando..." : "Ver recorrido"}
          </ReportActionButton>
          <ReportActionButton
            type="submit"
            disabled={loading || !filtersReadyForReport || disableWithoutCompany}
          >
            {loading ? "Generando..." : "Generar Reporte"}
          </ReportActionButton>
        </div>
      </form>

      {backup && (
        <div
          role="status"
          className={`mt-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border px-4 py-3 ${
            isDarkMode ? "border-amber-500/30 bg-amber-500/10" : "border-amber-200 bg-amber-50"
          }`}
        >
          <i className="ri-archive-line text-xl text-amber-500 flex-shrink-0" aria-hidden />
          <p className={`text-sm flex-1 ${themeClasses.text}`}>
            <strong>Parte de este rango está en un respaldo.</strong>{" "}
            {backup.devices === 1 ? "Un equipo tiene" : `${backup.devices} equipos tienen`} del {dmy(backup.firstDay)} al {dmy(backup.lastDay)} archivado
            (la cuenta se suspendió o se dio de baja): esas fechas no salen en este informe.
            {!isReseller && " Pide el respaldo a soporte."}
          </p>
          {isReseller && (
            <Link
              to={`/respaldos/${backup.jobIds[0]}`}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium bg-amber-500 text-white hover:bg-amber-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
            >
              Ver en Respaldos <i className="ri-arrow-right-line" aria-hidden />
            </Link>
          )}
        </div>
      )}

      {showSkeleton && (
        <div className={`mt-4 flex flex-col gap-4 ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-4`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="inline-block w-3 h-3 rounded-full bg-primary-500 animate-pulse" />
              <span className={`h-3 w-28 rounded animate-pulse ${isDarkMode ? "bg-white/10" : "bg-black/10"}`} />
              <span className={`h-3 w-20 rounded animate-pulse ${isDarkMode ? "bg-white/10" : "bg-black/10"}`} />
            </div>
            <span className={`h-3 w-16 rounded animate-pulse ${isDarkMode ? "bg-white/10" : "bg-black/10"}`} />
          </div>

          <div
            className={`relative w-full overflow-hidden rounded-lg ${isDarkMode ? "bg-white/5" : "bg-black/5"}`}
            style={{ height: 420 }}
          >
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
              <div className="relative flex items-center justify-center w-20 h-20">
                <span className="absolute inline-flex w-full h-full rounded-full bg-primary-500/20 animate-ping" />
                <span className="absolute inline-flex w-12 h-12 rounded-full border-4 border-primary-500 border-t-transparent animate-spin" />
                <span className="relative inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary-500/15">
                  <i className="ri-map-pin-line text-primary-500 text-2xl animate-bounce" />
                </span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <span className={`text-sm font-semibold ${themeClasses.text}`}>Trazando el recorrido…</span>
                <span className={`text-xs ${themeClasses.text} opacity-70`}>
                  Obteniendo posiciones del rango seleccionado
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className={`h-4 w-full rounded animate-pulse ${isDarkMode ? "bg-white/10" : "bg-black/10"}`}
                style={{ animationDelay: `${i * 120}ms` }}
              />
            ))}
          </div>
        </div>
      )}

      {hasResults && !showSkeleton && (
        <div className={`mt-4 flex flex-col gap-4 ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-4`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              {orderedRoutes.map((r) => (
                <div key={r.deviceId} className="flex items-center gap-2">
                  <span
                    className="inline-block w-3 h-3 rounded-full"
                    style={{ backgroundColor: r.color }}
                  />
                  <span className={`text-sm ${themeClasses.text}`}>
                    {r.vehicleName}
                    {r.loading
                      ? " (cargando...)"
                      : r.error
                        ? " (error)"
                        : ` (${r.points.length})`}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-sm ${themeClasses.text}`}>
                {totalPoints} puntos
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => onDownload("pdf")}
                  disabled={exporting !== null}
                  className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} disabled:opacity-50`}
                >
                  {exporting === "pdf" ? "Generando..." : "Descargar PDF"}
                </button>
                <button
                  type="button"
                  onClick={() => onDownload("excel")}
                  disabled={exporting !== null}
                  className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} disabled:opacity-50`}
                >
                  {exporting === "excel" ? "Generando..." : "Descargar Excel"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    clearTracking();
                    setRouteLoadAttempted(false);
                    setSelectedPoint(null);
                  }}
                  className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} ${themeClasses.buttonText} hover:opacity-80`}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>

          <p className={`text-xs ${themeClasses.textSecondary}`}>
            Pasa el cursor sobre la ruta para ver la hora de cada punto, haz clic en la ruta para fijar el detalle, o haz clic en una fila de la tabla para ubicarla en el mapa.
          </p>

          {orderedRoutes.length === 1 && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              {[
                { color: "#22C55E", label: "En movimiento" },
                { color: "#F59E0B", label: "Ralentí" },
                { color: "#EF4444", label: "Detenido" },
              ].map((s) => (
                <div key={s.label} className="flex items-center gap-1.5">
                  <span className="inline-block w-3 h-1.5 rounded-full" style={{ backgroundColor: s.color }} />
                  <span className={`text-[11px] ${themeClasses.textSecondary}`}>{s.label}</span>
                </div>
              ))}
            </div>
          )}

          <div className={`-mx-3 sm:-mx-4 px-3 sm:px-4 py-2 ${themeClasses.cardBg} border-b ${themeClasses.cardBorder}`}>
            <ReportRouteMap
              routes={mapRoutes}
              tz={tz || undefined}
              selected={selectedPoint}
              onSelect={setSelectedPoint}
              onClearSelected={clearSelectedPoint}
            />
          </div>

          <ReportPointsTable
            data={tableRows}
            columns={tableColumns}
            isDarkMode={isDarkMode}
            emptyText="No se encontraron posiciones en el rango seleccionado."
            onRowClick={onRowClick}
            isRowSelected={isRowSelected}
          />
        </div>
      )}

      {showEmptyState && (
        <div className={`mt-4 flex flex-col gap-4 ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-4`}>
          <div
            className={`relative w-full overflow-hidden rounded-xl border border-dashed ${themeClasses.cardBorder} ${isDarkMode ? "bg-white/5" : "bg-black/[0.02]"}`}
            style={{ minHeight: 320 }}
          >
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
              <span className={`inline-flex items-center justify-center w-14 h-14 rounded-full ${isDarkMode ? "bg-primary-500/15" : "bg-primary-500/10"}`}>
                <i className="ri-map-2-line text-primary-500 text-2xl" />
              </span>
              <div>
                <p className={`text-sm font-semibold ${themeClasses.text}`}>
                  Mapa y tabla de posiciones
                </p>
                <p className={`text-xs mt-1 max-w-md ${themeClasses.textSecondary}`}>
                  Configura los filtros y usa <strong>Ver recorrido</strong> para visualizar las posiciones aquí en el mapa y en la tabla.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className={hintCardClass(filtersReadyForRoute)}>
              <div className="flex items-start space-x-2">
                <i className={`ri-route-line text-base ${filtersReadyForRoute ? "text-primary-500" : themeClasses.textSecondary}`} />
                <div className="flex-1 min-w-0">
                  <p className={hintTitleClass(filtersReadyForRoute)}>Ver recorrido</p>
                  <p className={`text-sm font-semibold ${themeClasses.text} mt-0.5`}>
                    Hasta {MAX_TRACKING_RANGE_DAYS} días (1 mes)
                  </p>
                  <p className={`text-xs mt-1 ${themeClasses.textSecondary}`}>
                    Muestra el recorrido en mapa interactivo y tabla con detalle de cada posición. Ideal para consultas rápidas.
                  </p>
                </div>
              </div>
            </div>

            <div className={hintCardClass(!isWithinTrackingLimit && hasValidRange)}>
              <div className="flex items-start space-x-2">
                <i className={`ri-file-download-line text-base ${!isWithinTrackingLimit && hasValidRange ? "text-primary-500" : themeClasses.textSecondary}`} />
                <div className="flex-1 min-w-0">
                  <p className={hintTitleClass(!isWithinTrackingLimit && hasValidRange)}>Generar reporte</p>
                  <p className={`text-sm font-semibold ${themeClasses.text} mt-0.5`}>
                    Sin límite de rango
                  </p>
                  <p className={`text-xs mt-1 ${themeClasses.textSecondary}`}>
                    Para rangos mayores a 1 mes el reporte se genera en segundo plano. Te avisaremos cuando esté listo para descargar.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className={hintCardClass(filterItems.every((item) => item.ok))}>
            <div className="flex items-start space-x-2">
              <i className={`ri-filter-3-line text-base ${filterItems.every((item) => item.ok) ? "text-primary-500" : themeClasses.textSecondary}`} />
              <div className="flex-1 min-w-0">
                <p className={hintTitleClass(filterItems.every((item) => item.ok))}>
                  {filterItems.every((item) => item.ok)
                    ? "Filtros listos"
                    : "Completa los filtros"}
                </p>
                <ul className="mt-2 space-y-1.5">
                  {filterItems.map((item) => (
                    <li key={item.label} className="flex items-start gap-2 text-xs">
                      <i
                        className={`mt-0.5 ${
                          item.ok ? "ri-checkbox-circle-fill text-primary-500" : "ri-close-circle-line text-red-400"
                        }`}
                      />
                      <span className={item.ok ? themeClasses.text : themeClasses.textSecondary}>
                        {item.label}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {routeLoadAttempted && !trackingLoading && (
            <div className={`p-3 rounded-xl border ${isDarkMode ? "bg-dark-card border-dark-border" : "bg-gray-100 border-gray-200"}`}>
              <div className="flex items-start space-x-2">
                <i className={`ri-information-line text-base ${themeClasses.textSecondary}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium ${themeClasses.textSecondary}`}>Sin resultados</p>
                  <p className={`text-sm ${themeClasses.text} mt-0.5`}>
                    No se encontraron posiciones en el rango seleccionado. Verifica equipos, fechas y vuelve a intentar, o usa <strong>Generar reporte</strong> para un análisis más amplio.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
